import React, { useEffect, useState } from 'react';
import {
  getRiTotalRoutes, getRiOriginMarkets, getRiAvgGti,
  getRiCorridors, getRiCongestionLeaderboard,
} from '../../api/auth';
import { fmtUsd } from '../../utils/format';
import { formatCorridorLabel, cleanLocationLabel } from './lib/labelFormat';
import { SkeletonAlert } from '../shared/Skeleton';

function fmtInt(n) {
  if (n == null || n === '') return null;
  return Number(n).toLocaleString();
}

const ICON_BASE = 'w-5 h-5 shrink-0';

function LightbulbIcon({ className = '' }) {
  return (
    <svg className={`${ICON_BASE} ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 18h0M9 21h6M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2v1.1h5v-1.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3Z" />
    </svg>
  );
}

function MapPinIcon({ className = '' }) {
  return (
    <svg className={`${ICON_BASE} ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function RouteIcon({ className = '' }) {
  return (
    <svg className={`${ICON_BASE} ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="6" cy="19" r="3" />
      <circle cx="18" cy="5" r="3" />
      <path d="M9 19h6a4 4 0 0 0 0-8h-6a4 4 0 0 1 0-8" />
    </svg>
  );
}

function AlertIcon({ className = '' }) {
  return (
    <svg className={`${ICON_BASE} ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function DollarIcon({ className = '' }) {
  return (
    <svg className={`${ICON_BASE} ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function CloseIcon({ className = '' }) {
  return (
    <svg className={`w-4 h-4 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default function AnalysisAlert({ clientId, range }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [data, setData] = useState({ loading: true });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setData({ loading: true });
    Promise.all([
      getRiTotalRoutes(clientId, { from, to }).catch(() => null),
      getRiOriginMarkets(clientId, { from, to }).catch(() => null),
      getRiAvgGti(clientId, { from, to }).catch(() => null),
      getRiCorridors(clientId, { sort: 'volume', from, to }).catch(() => null),
      getRiCongestionLeaderboard(clientId, { from, to }).catch(() => null),
    ]).then(([routes, markets, gti, corridors, congestion]) => {
      if (cancelled) return;
      setData({
        loading: false,
        routes: routes?.data || null,
        markets: markets?.data || null,
        gti: gti?.data || null,
        topCorridor: corridors?.data?.[0] || null,
        worstCongested: congestion?.data?.[0] || null,
      });
    });
    return () => { cancelled = true; };
  }, [clientId, from, to]);

  const { loading, routes, markets, gti, topCorridor, worstCongested } = data;
  if (dismissed) return null;
  // Shimmer skeleton banner while the findings load — same treatment as the
  // page skeleton, instead of the old "Analyzing route data…" text.
  if (loading) return <SkeletonAlert lines={3} />;

  const insights = [];
  if (routes?.total_routes) {
    const reach = Number(routes.total_routes);
    const unreach = Number(routes.unreachable || 0);
    const total = reach + unreach;
    // Don't let rounding hit 100% while routes are still unreachable — a few
    // failed geocodes out of thousands rounds up to "100% reachable" and
    // contradicts the "N couldn't be geocoded" clause (markup #81). Cap at
    // 99.9 and show one decimal so the two halves stay consistent.
    const reachPct = total > 0 ? Math.min(99.9, (reach / total) * 100) : null;
    insights.push({
      Icon: MapPinIcon,
      iconClass: 'text-sky-500 dark:text-sky-400',
      label: 'Coverage',
      text: (
        <>
          <strong>{fmtInt(total)}</strong> guest routes analyzed
          {reachPct != null && unreach > 0 && (
            <> — <strong>{reachPct.toFixed(1)}%</strong> reachable — <strong>{fmtInt(unreach)}</strong> couldn't be geocoded</>
          )}
          {markets?.unique_states && (
            <> across <strong>{fmtInt(markets.unique_states)}</strong> states / <strong>{fmtInt(markets.unique_cities)}</strong> cities</>
          )}
        </>
      ),
    });
  }

  if (topCorridor) {
    insights.push({
      Icon: RouteIcon,
      iconClass: 'text-emerald-500 dark:text-emerald-400',
      label: 'Top corridor',
      text: (
        <>
          <strong>{formatCorridorLabel(topCorridor.corridor)}</strong> moves the most guests
          {topCorridor.volume != null && <> (<strong>{fmtInt(topCorridor.volume)}</strong> routes)</>}
          {Number(topCorridor.avg_booking_value) > 0 && (
            <> · avg booking <strong>{fmtUsd(topCorridor.avg_booking_value, { cents: true })}</strong></>
          )}
        </>
      ),
    });
  }

  if (worstCongested?.origin_label) {
    insights.push({
      Icon: AlertIcon,
      iconClass: 'text-red-500 dark:text-red-400',
      label: 'Congestion hotspot',
      text: (
        <>
          Guests from <strong>{cleanLocationLabel(worstCongested.origin_label)}</strong> lose the most time —
          {' '}<strong>{Number(worstCongested.congestion_minutes).toFixed(1)} min</strong>
          {worstCongested.congestion_tax_pct != null && (
            <> (<strong>{Number(worstCongested.congestion_tax_pct).toFixed(1)}%</strong> of drive time)</>
          )}
        </>
      ),
    });
  }

  if (gti?.booked_guests > 0 && gti?.avg_gti) {
    insights.push({
      Icon: DollarIcon,
      iconClass: 'text-amber-500 dark:text-amber-400',
      label: 'Trip economics',
      text: (
        <>
          Avg guest trip investment is <strong>{fmtUsd(gti.avg_gti)}</strong>
          {gti.avg_site_cost != null && gti.avg_fuel_cost != null && (
            <> (site <strong>{fmtUsd(gti.avg_site_cost)}</strong> + fuel <strong>{fmtUsd(gti.avg_fuel_cost)}</strong>)</>
          )}
          {' '}across <strong>{fmtInt(gti.booked_guests)}</strong> booked guests with route data
        </>
      ),
    });
  }

  if (insights.length === 0) return null;

  return (
    <div className="relative rounded-xl border border-[#32B1FF]/30 bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10 p-4">
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        title="Dismiss"
        className="absolute top-2 right-2 p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/40 dark:hover:bg-gray-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
      >
        <CloseIcon />
      </button>
      <div className="flex items-center gap-2 mb-3 pr-8">
        <LightbulbIcon className="text-amber-500 dark:text-amber-400" />
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          Key Findings
        </span>
      </div>
      <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
        {insights.map((i, idx) => {
          const { Icon, iconClass, label, text } = i;
          return (
            <li key={idx} className="flex items-start gap-2.5">
              <Icon className={`mt-0.5 ${iconClass}`} />
              <span><strong className="text-gray-900 dark:text-white">{label}:</strong> {text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
